/**
 * Sources / Default / Components / Custom / Hero
 *
 * @copyright 2026 i-fabrik GmbH
 * @author Tobias Garz
 */

import {extend} from '../../../../shared/utils';

import SelectorEngine from '../../../../shared/dom/selector-engine';
import Manipulator    from '../../../../shared/dom/manipulator';

// -------
// Private
// -------

const NAME = 'hero-slider';

const DEFAULTS = {
	autoHeight: true,
	autoplay: {
		delay: 5000,
		disableOnInteraction: false
	},
	loop: true
};

// reset all videos so they start at the beginning
const prepareVideos = (element) => {
	const videos = SelectorEngine.find('video', element);

	if(!videos.length) return;

	for(const video of videos) {
		video.currentTime = 0;
	}
};

// check media type for video or image
const checkSlideForMediaType = (element) => {
	return Manipulator.getDataAttribute(element, 'media-type');
};

// handle current slide depending on media type
const handleSlide = (element, sliderInstance) => {
	const type= checkSlideForMediaType(element);

	if(type === 'video') {
		handleVideo(element, sliderInstance);
	} else {
		sliderInstance.autoplay.start();
	}
};

// handle video elements because they break autoplay
// videos have to be fully played
const handleVideo = (element, sliderInstance) => {
	const video = SelectorEngine.findOne('video', element);

	if(!video) return;

	sliderInstance.autoplay.stop();
	video.muted = true;
	video.play();

	if(sliderInstance.slides.length > 1) {
		video.addEventListener('ended', () => {
			sliderInstance.slideNext();
		});
	} else {
		video.addEventListener('ended', () => {
			video.currentTime = 0;
			video.play();
		});
	}
};

const initSlider = (element) => {
	const sliderElement = SelectorEngine.findOne(`.${NAME}`, element);

	if(!sliderElement) return;

	const _options = extend({}, DEFAULTS, {
		navigation: {
			nextEl: SelectorEngine.findOne('.swiper-button-next', element) || null,
			prevEl: SelectorEngine.findOne('.swiper-button-prev', element) || null
		},
		on: {
			init: (instance) => {
				instance.autoplay.stop();
				handleSlide(instance.slides[instance.activeIndex], instance);
			},
			slideChange: (instance) => {
				prepareVideos(sliderElement);
				handleSlide(instance.slides[instance.activeIndex], instance);
			}
		}
	});

	const api = new Swiper(sliderElement, _options);
};

// -------
// Public
// -------

const init = () => {
	const slider = SelectorEngine.findOne(`[data-c="${NAME}"]`);

	if(slider) initSlider(slider);
};

// -------
// Export
// -------

export default {
	init: init
};